import { useState, useEffect, useCallback, useRef } from "react";
import axios from "../utils/axios";

/**
 * Custom hook for managing paginated data with search
 *
 * @param {string} endpoint - API endpoint to fetch data from
 * @param {Object} options - Configuration options
 * @param {number} options.initialLimit - Initial items per page (default: 20)
 * @param {Object} options.defaultFilters - Default filters to apply
 * @param {string} options.defaultSortBy - Default sort field
 * @param {string} options.defaultSortOrder - Default sort order (ASC/DESC)
 * @param {number} options.debounceDelay - Search debounce delay in ms (default: 500)
 * @param {boolean} options.autoFetch - Auto fetch on mount (default: true)
 *
 * @returns {Object} - { data, pagination, loading, error, fetchData, setPage, setLimit, setSearch, setFilters, setSorting, refresh }
 */
export const usePagination = (endpoint, options = {}) => {
  const {
    initialLimit = 20,
    defaultFilters = {},
    defaultSortBy = "created_at",
    defaultSortOrder = "DESC",
    debounceDelay = 500,
    autoFetch = true,
  } = options;

  // State management
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: initialLimit,
    total: 0,
    totalPages: 1,
    hasNext: false,
    hasPrev: false,
  });
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState(defaultFilters);
  const [sortBy, setSortBy] = useState(defaultSortBy);
  const [sortOrder, setSortOrder] = useState(defaultSortOrder);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch data function - use refs to avoid dependency issues
  const filtersRef = useRef(filters);
  const searchRef = useRef(search);
  const sortByRef = useRef(sortBy);
  const sortOrderRef = useRef(sortOrder);
  const isInitialMount = useRef(true);
  const isFetchingRef = useRef(false);

  // Update refs when values change
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  useEffect(() => {
    searchRef.current = search;
  }, [search]);

  useEffect(() => {
    sortByRef.current = sortBy;
  }, [sortBy]);

  useEffect(() => {
    sortOrderRef.current = sortOrder;
  }, [sortOrder]);

  // Fetch data function
  const fetchData = useCallback(async () => {
    // Prevent duplicate fetches
    if (isFetchingRef.current) {
      return;
    }

    isFetchingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      // Build query parameters
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        search: searchRef.current,
        sortBy: sortByRef.current,
        sortOrder: sortOrderRef.current,
        ...filtersRef.current,
      };

      // Remove empty values
      Object.keys(params).forEach((key) => {
        if (
          params[key] === "" ||
          params[key] === null ||
          params[key] === undefined
        ) {
          delete params[key];
        }
      });

      const response = await axios.get(endpoint, { params });

      // Handle different response structures
      const responseData = response.data;

      // Extract data - try different common key names
      const dataKeys = [
        "leads",
        "orders",
        "products",
        "appointments",
        "campaigns",
        "communications",
        "tasks",
        "data",
      ];

      let extractedData = [];
      for (const key of dataKeys) {
        if (responseData[key]) {
          extractedData = responseData[key];
          break;
        }
      }

      // Update state
      setData(extractedData);

      if (responseData.pagination) {
        setPagination((prev) => ({
          ...prev,
          ...responseData.pagination,
        }));
      }
    } catch (err) {
      console.error("Error fetching paginated data:", err);
      setError(err.message || "Failed to fetch data");
      setData([]);
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, [endpoint, pagination.page, pagination.limit]);

  // Effect to fetch data when page or limit changes
  useEffect(() => {
    if (autoFetch) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, pagination.limit, autoFetch]);

  // Effect to handle search and filter changes with debounce
  useEffect(() => {
    if (!autoFetch) return;

    // Skip on initial mount - let the page/limit effect handle it
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // Only fetch if there's actual search/filter content
    const hasSearch = search !== "";
    const hasFilters = Object.keys(filters).some(
      (key) =>
        filters[key] !== "" &&
        filters[key] !== null &&
        filters[key] !== undefined
    );

    if (!hasSearch && !hasFilters) return;

    // Reset to page 1 and fetch after debounce
    const timeoutId = setTimeout(() => {
      setPagination((prev) => {
        if (prev.page === 1) {
          // Already on page 1, just trigger fetch
          fetchData();
          return prev;
        }
        // Page change will trigger fetch via the other effect
        return { ...prev, page: 1 };
      });
    }, debounceDelay);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, filters, autoFetch, debounceDelay]);

  // Handler functions
  const setPage = (newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const setLimit = (newLimit) => {
    setPagination((prev) => ({ ...prev, limit: newLimit, page: 1 }));
  };

  const handleSearchChange = (searchValue) => {
    setSearch(searchValue);
    // Page reset happens in debounced fetch
  };

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
    // Page reset happens in debounced fetch
  };

  const handleSortingChange = (newSortBy, newSortOrder) => {
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const refresh = () => {
    fetchData();
  };

  return {
    data,
    pagination,
    loading,
    error,
    fetchData,
    setPage,
    setLimit,
    setSearch: handleSearchChange,
    setFilters: handleFiltersChange,
    setSorting: handleSortingChange,
    refresh,
    searchValue: search,
  };
};

export default usePagination;
